import type { PlayingCard, BaccaratBetType } from '@/types'
import { shuffleArray } from '@/lib/utils'

// Create a deck of cards
export function createDeck(): PlayingCard[] {
  const suits: PlayingCard['suit'][] = ['♠', '♥', '♦', '♣']
  const values: PlayingCard['value'][] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K']
  const deck: PlayingCard[] = []

  // Use 8 decks like in real baccarat
  for (let d = 0; d < 8; d++) {
    for (const suit of suits) {
      for (const value of values) {
        deck.push({
          suit,
          value,
          color: (suit === '♥' || suit === '♦') ? 'red' : 'black',
        })
      }
    }
  }

  return shuffleArray(deck)
}

// Get card value in baccarat (10, J, Q, K = 0)
export function getCardValue(card: PlayingCard): number {
  if (['10', 'J', 'Q', 'K'].includes(card.value)) return 0
  if (card.value === 'A') return 1
  return parseInt(card.value)
}

// Calculate hand value (only last digit)
export function calculateHandValue(hand: PlayingCard[]): number {
  const total = hand.reduce((sum, card) => sum + getCardValue(card), 0)
  return total % 10
}

// Check if it's a natural (8 or 9)
export function isNatural(hand: PlayingCard[]): boolean {
  const value = calculateHandValue(hand)
  return value >= 8
}

// Banker drawing rules (tableau)
export function shouldBankerDraw(bankerValue: number, playerThirdCard: PlayingCard | null): boolean {
  // If player didn't draw third card
  if (!playerThirdCard) {
    return bankerValue <= 5
  }

  const playerThirdValue = getCardValue(playerThirdCard)

  if (bankerValue <= 2) return true
  if (bankerValue === 3 && playerThirdValue !== 8) return true
  if (bankerValue === 4 && [2, 3, 4, 5, 6, 7].includes(playerThirdValue)) return true
  if (bankerValue === 5 && [4, 5, 6, 7].includes(playerThirdValue)) return true
  if (bankerValue === 6 && [6, 7].includes(playerThirdValue)) return true

  return false
}

// Determine winner
export function determineWinner(
  playerHand: PlayingCard[],
  bankerHand: PlayingCard[]
): BaccaratBetType {
  const playerValue = calculateHandValue(playerHand)
  const bankerValue = calculateHandValue(bankerHand)

  if (playerValue > bankerValue) return 'player'
  if (bankerValue > playerValue) return 'banker'
  return 'tie'
}

// Calculate payout
export function calculatePayout(
  betType: BaccaratBetType,
  winner: BaccaratBetType,
  betAmount: number
): { winnings: number; isWin: boolean; isTieReturn: boolean } {
  // Win case
  if (betType === winner) {
    // All bets pay 1:1 in this version
    return {
      winnings: betAmount * 2,
      isWin: true,
      isTieReturn: false,
    }
  }

  // Tie case - if player didn't bet on tie, they get their money back
  if (winner === 'tie' && betType !== 'tie') {
    return {
      winnings: betAmount,
      isWin: false,
      isTieReturn: true,
    }
  }

  // Loss
  return {
    winnings: 0,
    isWin: false,
    isTieReturn: false,
  }
}

// Format card display
export function formatCard(card: PlayingCard): string {
  return `${card.value}${card.suit}`
}
